import React, {FC} from 'react';
import {useQuery} from '@tanstack/react-query';
import parseISO from 'date-fns/parseISO';
import {useNavigate} from 'react-router-dom';
import RequestTimeframes from '../../../enums/RequestTimeframes';
import useMe from '../../../hooks/useMe';
import {Glum} from '../../../puddleglum';
import {AppointmentDate} from '../../../utils/format';
import EmptyState from 'components/EmptyState';
import RowActionMenu from 'components/RowActionMenu';
import {
	Badge,
	Box,
	Button,
	Heading,
	HStack,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useBreakpointValue,
	VStack,
} from 'components/ui/chakra';
import BookingController from 'puddleglum/api/patient/BookingController';

const MyAppointments: FC = () => {
	const navigate = useNavigate();
	const {me} = useMe();

	const {data: bookings, isLoading} = useQuery(['bookings'], async () => {
		const {data} = await BookingController.index({
			include_recent_cancellations: 1,
		});
		return data;
	});

	const editBookingRequest = (booking: Glum.Models.BookingRequest) => {
		navigate('/booking-request', {
			state: {
				booking_request: booking,
				reason: booking.reason,
				organization_id: booking.organization_id,
				provider_id: booking.provider_id,
				location_id: booking.location_id,
				treatment_ids: booking.orders?.map((o) => o.treatment_id),
				treatment_group_ids: booking.orders?.map((o) => o.treatment_group_id),
			},
		});
	};

	const getStatusBadge = (booking: Glum.Models.BookingRequest) => {
		if (booking.status === 'confirmed') {
			return <Badge colorScheme='green'>Confirmed</Badge>;
		}
		if (booking.status === 'pending') {
			return <Badge colorScheme='orange'>Pending</Badge>;
		}
		return <Badge colorScheme='gray'>Canceled</Badge>;
	};

	const getAppointmentContext = (booking: Glum.Models.BookingRequest) => {
		const type = booking.type?.trim();
		const reason = booking.reason?.trim();

		if (type && reason) return `${type} - ${reason}`;
		if (type) return type;
		if (reason) return reason;

		return 'Appointment details pending';
	};

	const isMobile = useBreakpointValue({base: true, md: false});

	if (isLoading || !bookings) return null;

	if (bookings.length === 0) {
		return (
			<Box w='full'>
				<Heading mb={4} size='md' color='gray.500'>
					Appointments
				</Heading>
				<EmptyState
					heading='No appointments yet'
					description='Once you book a visit, your upcoming and recently canceled appointments will show up here.'
				/>
			</Box>
		);
	}

	// Mobile card view
	if (isMobile) {
		return (
			<Box w='full'>
				<Heading mb={4} size='md' color='gray.500'>
					Appointments
				</Heading>
				<VStack spacing={3} w='full'>
					{bookings.map((booking) => {
						const isForDependent = booking.user && booking.user.id !== me?.id;

						return (
							<Box
								key={booking.id}
								w='full'
								p={4}
								bgColor={booking.status === 'pending' ? 'yellow.50' : 'white'}
								borderRadius='md'
								border='solid 1px'
								borderColor='gray.200'
							>
								<VStack align='stretch' spacing={3}>
									<HStack justify='space-between'>
										<Text fontSize='sm' color='gray.500' fontWeight='medium'>
											{booking.starts_at
												? AppointmentDate(parseISO(booking.starts_at))
												: RequestTimeframes[
														booking.requested_timeframe as keyof typeof RequestTimeframes
													]}
										</Text>
										<HStack>
											{getStatusBadge(booking)}
											{booking.needs_review && (
												<Badge colorScheme='yellow' fontSize='xs'>
													Review
												</Badge>
											)}
										</HStack>
									</HStack>

									<Box>
										{isForDependent && booking.user && (
											<Badge colorScheme='blue' fontSize='xs' mb={1}>
												{booking.user.first_name} {booking.user.last_name}
											</Badge>
										)}
										<Text fontSize='md' fontWeight='semibold'>
											{booking.organization?.name}
										</Text>
										<Text fontSize='sm' color='gray.600' noOfLines={2}>
											{getAppointmentContext(booking)}
										</Text>
										{booking.provider && (
											<Text fontSize='sm' color='gray.500'>
												{booking.provider.name}
											</Text>
										)}
									</Box>

									<HStack spacing={2}>
										{booking.status !== 'canceled' && (
											<Button
												size='sm'
												colorScheme='blue'
												onClick={() => editBookingRequest(booking)}
												flex={1}
											>
												{booking.status === 'confirmed' ? 'View' : 'Change'}
											</Button>
										)}
										<Button
											size='sm'
											variant='outline'
											as='a'
											href={`tel:${booking.organization?.phone}`}
											flex={1}
										>
											Call
										</Button>
									</HStack>
								</VStack>
							</Box>
						);
					})}
				</VStack>
			</Box>
		);
	}

	// Desktop table view
	return (
		<Box w='full'>
			<Heading mb={4} size='md' color='gray.500'>
				Appointments
			</Heading>
			<Box
				overflowX='auto'
				bgColor='white'
				borderRadius='md'
				border='solid 1px'
				borderColor='gray.200'
			>
				<Table size='sm'>
					<Thead bgColor='gray.50'>
						<Tr>
							<Th>Date/Time</Th>
							<Th>Provider</Th>
							<Th>Status</Th>
							<Th>Actions</Th>
						</Tr>
					</Thead>
					<Tbody>
						{bookings.map((booking) => {
							const isForDependent = booking.user && booking.user.id !== me?.id;

							return (
								<Tr
									key={booking.id}
									bgColor={booking.status === 'pending' ? 'yellow.50' : 'white'}
									_hover={{bgColor: 'gray.50'}}
								>
									<Td>
										<Text fontSize='sm' fontWeight='medium'>
											{booking.starts_at
												? AppointmentDate(parseISO(booking.starts_at))
												: RequestTimeframes[
														booking.requested_timeframe as keyof typeof RequestTimeframes
													]}
										</Text>
									</Td>
									<Td>
										<VStack align='start' spacing={0}>
											{isForDependent && booking.user && (
												<Badge colorScheme='blue' fontSize='xs' mb={1}>
													{booking.user.first_name}{' '}
													{booking.user.last_name}
												</Badge>
											)}
											<Text fontSize='sm' fontWeight='medium' noOfLines={2}>
												{booking.organization?.name}
											</Text>
											<Text fontSize='xs' color='gray.600' noOfLines={2}>
												{getAppointmentContext(booking)}
											</Text>
											{booking.provider && (
												<Text fontSize='xs' color='gray.500'>
													{booking.provider.name}
												</Text>
											)}
										</VStack>
									</Td>
									<Td>
										<VStack align='start' spacing={1}>
											{getStatusBadge(booking)}
											{booking.needs_review && (
												<Badge colorScheme='yellow' fontSize='xs'>
													Review
												</Badge>
											)}
										</VStack>
									</Td>
									<Td>
										<RowActionMenu
											ariaLabel={`Actions for appointment ${booking.id}`}
											items={[
												{
													label:
														booking.status === 'confirmed'
															? 'View appointment'
															: 'Change appointment',
													onClick: () => editBookingRequest(booking),
													hidden: booking.status === 'canceled',
												},
												{
													label: 'Call office',
													onClick: () => {
														window.location.href = `tel:${booking.organization?.phone}`;
													},
												},
											]}
										/>
									</Td>
								</Tr>
							);
						})}
					</Tbody>
				</Table>
			</Box>
		</Box>
	);
};

export default MyAppointments;
